import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade2Pinyin.css";

/** Shuffle helper */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade2ChineseLesson:
 *  - 2 Topics:
 *    1) Combining Pinyin into Words
 *    2) Basic Vocabulary & Definitions
 *  - Each has 25 quiz Q => total 50 final quiz
 *  - 2-column layout: left = lesson content, right = quick + final quiz
 */
export default function Grade2ChineseLesson() {
    // The user chooses a topic => store in state
    const [selectedTopic, setSelectedTopic] = useState(null);
  
    // (1) Lesson Content
    const lessonContent = {
      pinyin: {
        title: "Pinyin (Advanced)",
        description: "Build on basic Pinyin knowledge: multi-syllable words, short phrases, and tone practice!",
        example: "Example: xiǎo lóng bāo (小笼包) means 'soup dumplings.'",
        steps: [
          "Review of combining initials & finals: expand to 2+ syllable words, e.g., diǎn xīn (点心, 'dim sum').",
          "Focus on correct tones: mismatch can lead to confusion (e.g. shì 'is' vs. shí 'ten').",
          "Try reading short phrases: wǒ xǐhuān chī miàntiáo (我喜欢吃面条, 'I like to eat noodles').",
          "Look out for tone sandhi (like 'yī' → 'yí' before a 4th tone). These small changes matter!",
          "Practice multi-syllable reading daily to grow confidence in Pinyin-based reading!",
        ],
      },
      zhuyin: {
        title: "Zhuyin (Advanced)",
        description: "Use Bopomofo for more complex words and short sentences in Mandarin!",
        example: "Example: ㄋㄧˇ ㄏㄠˇ (你 好) 'hello,' plus extended phrases!",
        steps: [
          "Review: each syllable in Zhuyin has an initial (e.g. ㄓ) + final (e.g. ㄠ) + tone mark (ˊ, ˇ, etc.).",
          "Start combining to form 2- or 3-character words: ㄒㄧㄠˇ ㄌㄨㄥˊ ㄅㄠ (小笼包).",
          "Pay attention to tone marks: ㄧˇ vs. ㄧˋ drastically changes meaning.",
          "Try reading short sentences: ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄖㄜˋ ㄍㄡˇ (我喜歡熱狗, 'I like hot dogs').",
          "Reading speed improves with practice—Zhuyin helps you internalize Mandarin sounds quickly!",
        ],
      },
    };
  
    // (2) Quiz Pools => 25 each => total 50
    // pinyin => 25 Q
    const pinyinPool = [
      {
        question: "Which Pinyin phrase means 'I like to eat noodles'?",
        correctAnswer: "wǒ xǐhuān chī miàntiáo",
        options: [
          "nǐ hǎo chī miàntiáo",
          "wǒ xǐhuān chī miàntiáo",
          "wǒ bù xǐhuān niúròu",
        ],
      },
      {
        question: "In 'xiǎo lóng bāo' (小笼包), which tones appear?",
        correctAnswer: "3rd, 2nd, 1st (xiǎo lóng bāo)",
        options: [
          "3rd, 2nd, 1st",
          "2nd, 2nd, 1st",
          "3rd, 4th, 2nd",
        ],
      },
      {
        question: "Tone sandhi example: 'yī bēi' sometimes becomes 'yí bēi' for smoother flow. Why?",
        correctAnswer: "The 1st tone 'yī' changes to 2nd tone before a 4th tone word",
        options: [
          "No reason, random rule",
          "The 1st tone changes to 2nd to avoid tonal clash",
          "They are spelled the same, no change",
        ],
      },
      {
        question: "If you see 'shì' vs 'shí', what’s the difference?",
        correctAnswer: "shì (4th tone) = 'is/are,' shí (2nd tone) = 'ten'",
        options: [
          "No difference at all",
          "shì = 'ten', shí = 'history'",
          "shì (4th) = 'is,' shí (2nd) = 'ten'",
        ],
      },
      {
        question: "Which multi-syllable word is correct for 'dim sum'?",
        correctAnswer: "diǎn xīn (点心)",
        options: [
          "diàn xīn (shop heart)",
          "diǎn xīn (点心)",
          "diǎn qǐng (点请)",
        ],
      },
      {
        question: "Which Pinyin for 'báicài' (白菜, 'bok choy') might you see?",
        correctAnswer: "bái (2nd tone) + cài (4th tone)",
        options: [
          "bái cài (2nd, 4th)",
          "bǎi cài (3rd, 4th)",
          "bái cài (4th, 2nd)",
        ],
      },
      {
        question: "'huǒguō' (火锅) means 'hot pot.' The tones are?",
        correctAnswer: "3rd + 1st (huǒ guō)",
        options: [
          "2nd + 1st",
          "3rd + 1st",
          "4th + neutral",
        ],
      },
      {
        question: "Which phrase means 'I can speak a little Chinese'?",
        correctAnswer: "wǒ huì shuō yīdiǎn zhōngwén",
        options: [
          "wǒ bù huì tīng yīngwén",
          "nǐ hěn máng ma",
          "wǒ huì shuō yīdiǎn zhōngwén",
        ],
      },
      {
        question: "For 'guānglín' (光临, polite 'presence'), which initials/finals are correct?",
        correctAnswer: "g + uang = guāng, l + in = lín",
        options: [
          "g + uan = guan, l + ing = ling",
          "g + uang = guāng, l + in = lín",
          "gu + ang, la + in, no tone",
        ],
      },
      {
        question: "Which word means 'mapo tofu' in pinyin?",
        correctAnswer: "mápó dòufu",
        options: [
          "mǎpó dòufǔ",
          "mápó dòufu",
          "mápu dúfu",
        ],
      },
      {
        question: "The phrase 'zěnme yàng' (怎么样) means?",
        correctAnswer: "'How is it?' or 'How about...?'.",
        options: [
          "'Where are you going?'",
          "'How is it?'",
          "'Sorry, I'm late.'",
        ],
      },
      {
        question: "Which Pinyin is correct for 'Shanghai'? (上海)",
        correctAnswer: "shànghǎi (4th, 3rd tones)",
        options: [
          "shanghǎi (no tone)",
          "shànghǎi (4th, 3rd)",
          "xiānghǎi (1st, 3rd)",
        ],
      },
      {
        question: "'píngguǒ' (苹果) means 'apple.' The tones are?",
        correctAnswer: "2nd + 3rd (píng guǒ)",
        options: [
          "2nd + 1st",
          "2nd + 3rd",
          "3rd + 2nd",
        ],
      },
      {
        question: "When reading a two-syllable word, do you pause between them?",
        correctAnswer: "No, but each syllable has its own tone—flow them smoothly",
        options: [
          "Yes, always a huge pause",
          "No, blend them smoothly but each keeps its tone",
          "They become one combined tone",
        ],
      },
      {
        question: "Which is a polysyllabic expression: 'hǎochī' (好吃) meaning?",
        correctAnswer: "'Tasty' or 'delicious'",
        options: [
          "'hungry'",
          "'Tasty/delicious'",
          "'thirsty'",
        ],
      },
      {
        question: "In Pinyin, 'chī' (吃) means 'to eat,' while 'qī' means 'seven.' This shows?",
        correctAnswer: "Similar final but different initials + tones = different words",
        options: [
          "They’re spelled the same",
          "No difference in meaning",
          "Similar final but different initials + tones = different words",
        ],
      },
      {
        question: "A sentence: 'nǐ yǒu shénme àihào?' means?",
        correctAnswer: "What hobbies do you have?",
        options: [
          "Where do you live?",
          "What hobbies do you have?",
          "How old are you?",
        ],
      },
      {
        question: "Which is a correct multi-syllable Pinyin for 'dumpling' (饺子)?",
        correctAnswer: "jiǎozi (3rd + neutral)",
        options: [
          "jiāozi (1st + neutral)",
          "jiǎozi (3rd + neutral)",
          "jiàozi (4th + neutral)",
        ],
      },
      {
        question: "'hěn hǎo' (很好) means?",
        correctAnswer: "'Very good'",
        options: [
          "'Not good'",
          "'Very good'",
          "'So-so'",
        ],
      },
      {
        question: "Which Pinyin phrase might mean 'What's up?' or 'What's the matter?'",
        correctAnswer: "zěnme le?",
        options: [
          "shénme yìsi?",
          "zěnme le?",
          "wèishénme?",
        ],
      },
      {
        question: "To say 'I want to buy this' in Pinyin: 'wǒ yào mǎi zhè gè.' The tones are?",
        correctAnswer: "3rd, 4th, 3rd, 4th, neutral",
        options: [
          "1st, 2nd, 1st, 1st",
          "3rd, 4th, 3rd, 4th, neutral",
          "2nd, 2nd, 2nd, 3rd, 4th",
        ],
      },
      {
        question: "'kàn shū' means 'to read a book.' 'kàn' is which tone?",
        correctAnswer: "4th tone (to look/read)",
        options: [
          "3rd tone",
          "2nd tone",
          "4th tone",
        ],
      },
      {
        question: "Which is correct for 'dōngxi' (东西, 'things/stuff')?",
        correctAnswer: "dōng (1st), xi (neutral or 1st) ~ dōngxi",
        options: [
          "dōng xī always 4th",
          "dōngxi (1st, neutral or 1st tone on second)",
          "dǒngxì (3rd, 4th)",
        ],
      },
      {
        question: "Why do multi-syllable words matter for advanced Pinyin learning?",
        correctAnswer: "They help practice tone transitions & common phrases",
        options: [
          "They have no meaning in real Chinese",
          "They help practice tone transitions & phrases",
          "They are too hard for kids",
        ],
      },
    ];
  
    // zhuyin => 25 Q
    const zhuyinPool = [
      {
        question: "If writing 小龙包 in Zhuyin, you might see?",
        correctAnswer: "ㄒㄧㄠˇ ㄌㄨㄥˊ ㄅㄠ",
        options: [
          "ㄒㄧㄠ ㄌㄨㄥㄅㄠˋ",
          "ㄒㄧㄠˇ ㄌㄨㄥˊ ㄅㄠ",
          "ㄒㄩˊ ㄌㄨㄥˋ ㄅㄢˇ",
        ],
      },
      {
        question: "How do you mark the 3rd tone in Zhuyin?",
        correctAnswer: "ˇ placed after or above the symbol",
        options: [
          "ˊ only",
          "ˇ placed after or above the symbol",
          "No tone mark needed",
        ],
      },
      {
        question: "Zhuyin for '我喜欢吃面条' might be?",
        correctAnswer: "ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄔ ㄇㄧㄢˋ ㄊㄧㄠˊ (approx.)",
        options: [
          "ㄨㄛˋ ㄕˋ ㄏㄨㄣˊ ㄔㄣ ㄇㄧㄣˋ ㄊㄨㄠˊ",
          "ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄔ ㄇㄧㄢˋ ㄊㄧㄠˊ",
          "ㄋㄧˇ ㄏㄠˇ ㄖㄣˊ ㄇㄧㄢˇ ㄊㄨㄥˊ",
        ],
      },
      {
        question: "In Zhuyin, 'ㄓ + ㄨㄥˊ' => 'ㄓㄨㄥˊ'? Could mean?",
        correctAnswer: "中 (zhōng) with 1st or 2nd tone, or zhóng with 2nd/3rd—context needed",
        options: [
          "No such combo",
          "Always 4th tone only",
          "中 (zhōng) or zhóng, context decides tone",
        ],
      },
      {
        question: "Tone mark 'ˋ' in Zhuyin indicates?",
        correctAnswer: "4th tone (falling)",
        options: [
          "1st tone (level)",
          "4th tone (falling)",
          "2nd tone (rising)",
        ],
      },
      {
        question: "If you see ㄒㄧㄝˋ ㄒㄧㄝ (谢谢), it means?",
        correctAnswer: "Thank you",
        options: [
          "Goodbye",
          "Thank you",
          "Excuse me",
        ],
      },
      {
        question: "A multi-character phrase like 我喜欢你 in Zhuyin might read?",
        correctAnswer: "ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄋㄧˇ",
        options: [
          "ㄨㄛˊ ㄕˇ ㄏㄨㄢˇ ㄋㄧˋ",
          "ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄋㄧˇ",
          "ㄨㄛˇ ㄐㄧ ㄏㄨㄢ ㄋㄧˇ",
        ],
      },
      {
        question: "Which symbol combination might represent 'lái' (来) in Zhuyin?",
        correctAnswer: "ㄌㄞˊ (2nd tone)",
        options: [
          "ㄌㄞˇ (3rd tone)",
          "ㄌㄟˊ (2nd tone)",
          "ㄌㄞˊ (2nd tone)",
        ],
      },
      {
        question: "Zhuyin 1st tone mark is typically?",
        correctAnswer: "No additional mark or a ˉ, written as a line above",
        options: [
          "ˇ",
          "ˋ",
          "no mark or ˉ",
        ],
      },
      {
        question: "If you see ㄓㄨˇ ㄧˋ in Zhuyin, it might correspond to 'zhǔyì' (主意) meaning?",
        correctAnswer: "'idea'",
        options: [
          "'topic'",
          "'idea'",
          "'weekend'",
        ],
      },
      {
        question: "How to form 'hot pot' 火锅 in Zhuyin?",
        correctAnswer: "ㄏㄨㄛˇ ㄍㄨㄛ (huǒ guō)",
        options: [
          "ㄏㄨㄢˇ ㄍㄨㄛˊ",
          "ㄏㄨㄛˇ ㄍㄨㄛ",
          "ㄏㄜˋ ㄍㄨㄛ",
        ],
      },
      {
        question: "When reading ㄨㄛˇ ㄑㄧㄥˇ ㄨㄣˋ (我请问)... means?",
        correctAnswer: "'May I ask...?' / 'Excuse me, I want to ask...'",
        options: [
          "I have no idea",
          "'May I ask...?'",
          "I'm going to eat",
        ],
      },
      {
        question: "Zhuyin for 'xiǎo' (小) might be?",
        correctAnswer: "ㄒㄧㄠˇ",
        options: [
          "ㄒㄧㄠ (no tone)",
          "ㄒㄧㄠˇ",
          "ㄑㄧㄠˇ",
        ],
      },
      {
        question: "Which Bopomofo parts might form 'ㄨㄛˇ' for 'wo' (I/me)?",
        correctAnswer: "ㄨ (u) + ㄛ (o) + ˇ = ㄨㄛˇ",
        options: [
          "ㄨ + ㄜ + ˊ",
          "ㄩ + ㄛ + ˇ",
          "ㄨ + ㄛ + ˇ",
        ],
      },
      {
        question: "A phrase like ㄨㄣˋ ㄊㄧˊ (问题) means?",
        correctAnswer: "'question' or 'problem'",
        options: [
          "'weather'",
          "'question/problem'",
          "'culture'",
        ],
      },
      {
        question: "If someone says ㄊㄢˊ ㄏㄨㄚˋ (谈话), they mean?",
        correctAnswer: "'to talk' or 'conversation'",
        options: [
          "'to walk slowly'",
          "'to talk/conversation'",
          "'to sleep early'",
        ],
      },
      {
        question: "ㄉㄧˇ ㄉㄧˇ might represent 'dǐdǐ.' But do we commonly see that word?",
        correctAnswer: "Might be '弟弟' (dìdi) = younger brother if spelled differently (4th tone).",
        options: [
          "Yes, always means father",
          "No usage in modern Chinese",
          "Could be 弟弟 (dìdi) with tone changes => younger brother",
        ],
      },
      {
        question: "Tone mark for 3rd tone in Zhuyin is?",
        correctAnswer: "ˇ (a v-like symbol above/after the syllable)",
        options: [
          "ˋ falling",
          "ˇ third tone",
          "no mark for 3rd tone",
        ],
      },
      {
        question: "Reading multi-character words in Zhuyin helps you?",
        correctAnswer: "Build fluency & recognize how syllables link in phrases",
        options: [
          "Confuse Pinyin rules",
          "Avoid real reading",
          "Build fluency & link syllables in phrases",
        ],
      },
      {
        question: "If you see ㄓ + ㄣˇ ㄇㄜ˙ => 'zhěnme' or 'zěnme'?",
        correctAnswer: "Often zěnme (3rd tone + neutral) spelled ㄗㄣˇ ㄇㄜ˙ in standard zhuyin",
        options: [
          "No such phrase",
          "Always zhěn me (4th tone)",
          "zěnme (3rd + neutral)",
        ],
      },
      {
        question: "Completing a short sentence: ㄨㄛˇ ㄞˋ ㄋㄧˇ => 'wǒ ài nǐ' means?",
        correctAnswer: "I love you",
        options: [
          "I see you",
          "I love you",
          "I have you",
        ],
      },
      {
        question: "Which could represent 'kuàilè' (快乐, 'happy') in Zhuyin?",
        correctAnswer: "ㄎㄨㄞˋ ㄌㄜˋ (4th + 4th tone)",
        options: [
          "ㄎㄨㄞˇ ㄌㄜˇ",
          "ㄎㄨㄞˋ ㄌㄜˋ",
          "ㄎㄩㄞˋ ㄌㄧˇ",
        ],
      },
      {
        question: "The symbol ㄕ + ㄨˊ => 'shú'? Possibly means '熟' (ripe/familiar). Which tone might it need?",
        correctAnswer: "2nd or 2rd? 'shú' is 2nd tone in standard usage (熟).",
        options: [
          "No tone needed",
          "3rd tone only",
          "2nd tone typically",
        ],
      },
      {
        question: "Why are multi-syllable words in Zhuyin important at Grade 2?",
        correctAnswer: "They reflect actual usage (short phrases, everyday words), building real reading skill",
        options: [
          "They are too advanced for kids",
          "They reflect actual usage & build real reading skill",
          "Zhuyin only works for single characters",
        ],
      },
    ];
  
    // (3) Quick Quiz & Final Quiz states
    const [quiz, setQuiz] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [feedback, setFeedback] = useState(null);
  
    const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
    const [finalQuizActive, setFinalQuizActive] = useState(false);
    const [finalQuizIndex, setFinalQuizIndex] = useState(0);
    const [finalQuizScore, setFinalQuizScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(300); // 5 min
  
    // (4) Timer effect for final quiz
    useEffect(() => {
      if (finalQuizActive && timeLeft > 0) {
        const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
        return () => clearTimeout(timer);
      } else if (timeLeft === 0) {
        setFinalQuizActive(false);
      }
    }, [finalQuizActive, timeLeft]);
  
    // (5) Quick quiz generator
    const generateQuiz = (topic) => {
      let questionItem;
      if (topic === "pinyin") {
        questionItem = pinyinPool[Math.floor(Math.random() * pinyinPool.length)];
      } else {
        questionItem = zhuyinPool[Math.floor(Math.random() * zhuyinPool.length)];
      }
      setQuiz({
        question: questionItem.question,
        correctAnswer: questionItem.correctAnswer,
        options: shuffleArray([...questionItem.options]),
      });
      setSelectedAnswer(null);
      setFeedback(null);
    };
  
    // (6) Quick quiz answer
    const handleAnswerClick = (answer) => {
      setSelectedAnswer(answer);
      if (quiz && answer === quiz.correctAnswer) {
        setFeedback("✅ Correct!");
      } else {
        setFeedback("❌ Try Again!");
      }
      setTimeout(() => {
        if (selectedTopic) generateQuiz(selectedTopic);
      }, 1500);
    };
  
    // (7) Start Final Quiz => merges 25 + 25 => 50
    const startFinalQuiz = () => {
      const combined = [...pinyinPool, ...zhuyinPool];
      const finalSet = shuffleArray(combined);
      setFinalQuizQuestions(finalSet);
      setFinalQuizActive(true);
      setFinalQuizIndex(0);
      setFinalQuizScore(0);
      setTimeLeft(300);
    };
  
    // (8) Final Quiz answer
    const handleFinalQuizAnswer = (choice) => {
      if (finalQuizQuestions[finalQuizIndex]?.correctAnswer === choice) {
        setFinalQuizScore(prev => prev + 1);
      }
      if (finalQuizIndex < finalQuizQuestions.length - 1) {
        setFinalQuizIndex(prev => prev + 1);
      } else {
        setFinalQuizActive(false);
      }
    };
  
    return (
      <div className="g2chi-page">
        <h1 className="g2chi-title">Grade 2 Chinese Lessons</h1>
        <p className="g2chi-description">Choose a system to explore: Pinyin or Zhuyin!</p>
  
        <div className="g2chi-topics">
          <button
            className="g2chi-topic-card"
            onClick={() => {
              setSelectedTopic("pinyin");
              generateQuiz("pinyin");
            }}
          >
            Pinyin (Advanced)
          </button>
          <button
            className="g2chi-topic-card"
            onClick={() => {
              setSelectedTopic("zhuyin");
              generateQuiz("zhuyin");
            }}
          >
            Zhuyin (Advanced)
          </button>
        </div>
  
        {selectedTopic && (
          <div className="g2chi-lesson-quiz-container">
            {/* LESSON CONTENT */}
            <div className="g2chi-lesson-content">
              <h2 className="g2chi-lesson-title">
                {lessonContent[selectedTopic].title}
              </h2>
              <p className="g2chi-lesson-description">
                {lessonContent[selectedTopic].description}
              </p>
              <p className="g2chi-lesson-example">
                <strong>{lessonContent[selectedTopic].example}</strong>
              </p>
              <ul className="g2chi-lesson-steps">
                {lessonContent[selectedTopic].steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            </div>
  
            {/* QUIZZES */}
            <div className="g2chi-quiz-double-container">
              {/* Quick Quiz */}
              <div className="g2chi-quick-quiz-container">
                <h2 className="g2chi-quiz-title">Quick Quiz!</h2>
                {quiz && (
                  <>
                    <p className="g2chi-quiz-question">{quiz.question}</p>
                    <div className="g2chi-quiz-options">
                      {quiz.options.map((opt, i) => (
                        <button
                          key={i}
                          className={`g2chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                          onClick={() => handleAnswerClick(opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                    {feedback && <p className="g2chi-quiz-feedback">{feedback}</p>}
                  </>
                )}
              </div>
  
              {/* Final Quiz */}
              <div className="g2chi-final-quiz-container">
                <h2 className="g2chi-quiz-title">Final Quiz</h2>
                {!finalQuizActive && (
                  <button className="g2chi-final-quiz-button" onClick={startFinalQuiz}>
                    Start Final Quiz (50 Questions)
                  </button>
                )}
  
                {finalQuizActive && (
                  <div className="g2chi-final-quiz-content">
                    <p>
                      Time Left: {Math.floor(timeLeft / 60)}:
                      {String(timeLeft % 60).padStart(2, "0")}
                    </p>
                    <p>Question {finalQuizIndex + 1} of {finalQuizQuestions.length}</p>
                    <p className="g2chi-quiz-question">
                      {finalQuizQuestions[finalQuizIndex].question}
                    </p>
                    <div className="g2chi-quiz-options">
                      {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                        <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                          {choice}
                        </button>
                      ))}
                    </div>
                    <p>Score: {finalQuizScore}</p>
                    {finalQuizIndex === finalQuizQuestions.length - 1 && (
                      <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
  
        <Link to="/chineseSubject" className="g2chi-back-button">
          ← Back to Chinese Page
        </Link>
      </div>
    );
  }